import { ArrowCounterClockwise, ChartBar, Key, Lock, PencilSimple, ShieldCheck, User } from '@phosphor-icons/react';
import type { StaffRole, StaffRow } from '@mybusinessmate/schema';
import type { RowMenuItem } from '../directory/row-menu';

/** The short role at the right of "Change role" (Figma A04.1 row menu shows "Lead"). */
const ROLE_HINT: Record<StaffRole, string> = {
  admin: 'Admin',
  account_manager: 'AM',
  lead_specialist: 'Lead',
  specialist: 'Specialist',
};

export interface StaffMenuActions {
  /** Left out on the person's own page, where "View profile" would only reload it. */
  onView?: () => void;
  onEdit: () => void;
  onSuspend: () => void;
  onReinstate: () => void;
  /** For what is drawn as designed but not built yet: answers with a "Coming soon" note. */
  onSoon: (feature: string) => void;
}

/**
 * What the "⋯" on a person offers (Figma A04.1 "Row menu"): the profile, their details, their role, a password reset,
 * handing their tasks around, and (admin, never on yourself) suspending or lifting access.
 * TODO(api): "Send password reset" and "Rebalance tasks" (dispatch, Figma A04.3) have no endpoints yet.
 */
export function staffMenuItems(
  person: Pick<StaffRow, 'name' | 'staffRole' | 'status' | 'activeTaskCount'>,
  isSelf: boolean,
  actions: StaffMenuActions,
): RowMenuItem[] {
  const suspended = person.status === 'suspended';
  return [
    ...(actions.onView ? [{ label: 'View profile', icon: User, onSelect: actions.onView }] : []),
    { label: 'Edit details', icon: PencilSimple, onSelect: actions.onEdit },
    { label: 'Change role', icon: ShieldCheck, hint: ROLE_HINT[person.staffRole], onSelect: actions.onEdit },
    { label: 'Send password reset', icon: Key, onSelect: () => actions.onSoon('Password resets') },
    {
      label: 'Rebalance tasks',
      icon: ChartBar,
      hint: `${person.activeTaskCount} open`,
      separated: true,
      onSelect: () => actions.onSoon('Rebalancing tasks'),
    },
    ...(suspended
      ? [{ label: 'Reinstate access', icon: ArrowCounterClockwise, onSelect: actions.onReinstate }]
      : isSelf
        ? []
        : [{ label: 'Suspend access', icon: Lock, onSelect: actions.onSuspend }]),
  ];
}
